/** The server functions tRPC cannot carry, such as streaming; everything else goes through `@/lib/trpc`. */

// MARK: Imports

import { createServerFn } from '@tanstack/react-start';
import { isServer } from '@tanstack/react-query';
import { currentBuildAssets, reportIsUnattributable, stackBundleIsCurrent } from './build-assets';
import { parseRequest } from './server-functions-shared.server';
import { eventEmitter } from './server-broadcast.server';
import { clientTelemetryContext, telemetryCaller } from './wide-event.server';
import { allowTelemetryActions, allowTelemetryErrorReport } from './telemetry-rate-limit.server';
import {
	PORTAL_FRONTEND_ERROR_MARKER,
	recordClientActions,
	recordClientError,
	reportEventError,
	reportRawError,
} from '@worldcoo/monolith/wide-events';
import {
	donationIdSchema,
	logErrorInputSchema,
	portalLocaleSchema,
	recordClientActionsInputSchema,
} from './data-schemas.server';
import type * as z from 'zod';
import { shouldReportFrontendError } from './frontend-error-filter';

// MARK: Types

export type PutProjectErrorCode = 'PROJECT_TOO_LARGE' | 'SAVE_PROJECT_FAILED';
export type TranslateErrorCode = 'UNAUTHORIZED' | 'CAPTCHA_FAILED' | 'TRANSLATION_FAILED';
export type ContractErrorCode = 'NETWORK_ERROR' | 'CAPTCHA_FAILED' | 'CONTRACT_GENERATION_FAILED';
export type DraftContractErrorCode = 'DRAFT_NOT_FOUND' | 'CONTRACT_GENERATION_FAILED';
export type OnboardingDraftErrorCode = 'NETWORK_ERROR' | 'SAVE_FAILED';
export type LiveCounterMode = 'day' | 'realtime';

// MARK: Shared helpers

export async function ensureSeoData(context: { isBot: boolean }, promise: Promise<unknown>): Promise<void> {
	if (isServer && context.isBot) {
		await promise;
	}
}

// Lives in `./live-window` so the tRPC server modules need not import this file; the www routes import it here.
export { currentWindowKey } from './live-window';

// MARK: Server functions called directly

export const getRequestContext = createServerFn()
	.validator(portalLocaleSchema)
	.handler(({ data }) => parseRequest(data));

export const logErrorFn = createServerFn({ method: 'POST' })
	.validator(logErrorInputSchema)
	.handler(({ data }) => {
		// The caller controls the fingerprint, so an unbounded open endpoint mints Jira issues. Silent, because a
		// failing call is one more console error; before the cookie and HMAC work a hammering script would cost.
		if (!allowTelemetryErrorReport(telemetryCaller())) {
			return { success: true, logged: false, ticketed: false };
		}

		// The ticket prints this same event, so both describe the crash identically. docs/notes/portals/data.md
		const bundleCurrent = stackBundleIsCurrent(data.stack, currentBuildAssets());
		const event = recordClientError({ ...clientTelemetryContext(), ...data, bundleCurrent });

		// A long-lived tab keeps an old filter across deploys (TEC-3648); the analytics row still counts it.
		if (!shouldReportFrontendError(data.name, data.message, data.stack, data.cause)) {
			return { success: true, logged: true, ticketed: false };
		}

		// Crawlers replaying one document across facet URLs (TEC-3854): not fixable here, still in analytics.
		if (data.documentReplayed) {
			return { success: true, logged: true, ticketed: false };
		}

		// A replayed document runs the old bundle, which cannot send `documentReplayed` (355 of 358 pro #418s in 14 days).
		if (reportIsUnattributable({ ...data, bundleCurrent })) {
			return { success: true, logged: true, ticketed: false };
		}

		// One line may carry the marker, since the parser tickets each occurrence; `report-line.ts` owns that rule.
		if (event) {
			reportEventError(event, { marker: PORTAL_FRONTEND_ERROR_MARKER });
		} else {
			reportRawError(PORTAL_FRONTEND_ERROR_MARKER, `${data.name} — ${data.message}`);
		}
		return { success: true, logged: true, ticketed: true };
	});

/**
 * Bounded per call and per caller, since each action is a queue message and a durable row; excess is dropped
 * silently so a failing call cannot pollute the signal. What the client may collect: `lib/session-actions.ts`.
 */
export const recordClientActionsFn = createServerFn({ method: 'POST' })
	.validator(recordClientActionsInputSchema)
	.handler(({ data }) => {
		const allowed = allowTelemetryActions(telemetryCaller(), data.actions.length);
		if (allowed > 0) {
			recordClientActions({
				...clientTelemetryContext(),
				actions: data.actions.slice(0, allowed),
				nowMs: Date.now(),
			});
		}
		return { success: true } as const;
	});

// MARK: Streaming server functions

export const waitForConfirmedDonationFn = createServerFn()
	.validator(donationIdSchema)
	.handler(async function* ({ data: donationIdToConfirm }) {
		const promise = new Promise<'donation.confirmed' | 'donation.timeout'>((resolve) => {
			const onConfirmed = (donationId: string) => {
				if (donationId === donationIdToConfirm) {
					eventEmitter.off('donation.confirmed', onConfirmed);
					resolve('donation.confirmed');
				}
			};
			eventEmitter.on('donation.confirmed', onConfirmed);
			setTimeout(() => {
				eventEmitter.off('donation.confirmed', onConfirmed);
				resolve('donation.timeout');
			}, 10_000);
		});
		yield await promise;
	});

// MARK: Utility functions

/** Covers Adyen's wrapper around a DOMException, and stops a cyclic chain. */
const MAX_CAUSE_DEPTH = 3;

/**
 * Without it an `AdyenCheckoutError` ticket says only "something went wrong". Its own field, because `message`
 * and `stack` feed `computeFingerprint` and grafted frames would read as foreign.
 */
function describeCauseChain(error: unknown): string | undefined {
	const parts: string[] = [];
	try {
		let current: unknown = error;
		for (let depth = 0; depth < MAX_CAUSE_DEPTH; depth++) {
			if (!(current instanceof Error)) break;
			const cause: unknown = current.cause;
			if (cause === undefined || cause === null) break;
			if (cause instanceof Error) {
				parts.push(`Caused by: ${cause.name}: ${cause.message}\n${cause.stack ?? '(no stack)'}`);
			} else {
				parts.push(`Caused by: ${typeof cause === 'string' ? cause : safeStringify(cause)}`);
				break;
			}
			current = cause;
		}
	} catch {
		// A failure to enrich a report must never lose the report.
	}
	return parts.length === 0 ? undefined : parts.join('\n\n');
}

export interface ExtractedErrorInfo {
	name: string;
	message: string;
	stack: string | undefined;
	cause?: string;
}

export function extractErrorInfo(error: unknown): ExtractedErrorInfo {
	if (error instanceof Error) {
		return {
			name: error.name || 'Error',
			message: error.message || '(empty message)',
			stack: error.stack,
			cause: describeCauseChain(error),
		};
	}
	if (typeof error === 'string') {
		return { name: 'StringError', message: error, stack: undefined };
	}
	if (error === null || error === undefined) {
		return { name: 'NullishError', message: `Thrown value was ${String(error)}`, stack: undefined };
	}
	if (typeof error === 'object') {
		const name = 'name' in error && typeof error.name === 'string' ? error.name : 'ObjectError';
		const message =
			'message' in error && typeof error.message === 'string'
				? error.message
				: 'error' in error && typeof error.error === 'string'
					? error.error
					: safeStringify(error);
		const stack = 'stack' in error && typeof error.stack === 'string' ? error.stack : undefined;
		return { name, message, stack };
	}
	return { name: 'UnknownError', message: safeStringify(error), stack: undefined };
}

/** One builder, so a new field is not a ten-site edit where a missed site silently reports less. */
export function errorReportData(
	namePrefix: string,
	info: ExtractedErrorInfo,
	extra?: Partial<z.input<typeof logErrorInputSchema>>,
): z.input<typeof logErrorInputSchema> {
	const { name, ...rest } = info;
	return { route: window.location.pathname, name: namePrefix + name, ...rest, ...extra };
}

function safeStringify(value: unknown): string {
	try {
		return JSON.stringify(value);
	} catch {
		return String(value);
	}
}

// The reporting predicates live in the dependency-free ./frontend-error-filter; import them from there.
